import { type ReactNode } from 'react';

import { Divider } from '@/components/Divider';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';

export interface DiagramPickerOption<T> {
  value: T;
  label: string;
  /** A 16px drawing of the option. The trigger shows the icon of the current option. */
  icon?: ReactNode;
}

export interface DiagramMenuButtonProps {
  /** The accessible name and the tooltip of the trigger. */
  label: string;
  /** The content of the trigger, such as an icon or text. */
  trigger: ReactNode;
  disabled?: boolean;
  /** The menu: `DiagramMenuOptions`, `DiagramMenuCheckbox`, and `Divider`. */
  children: ReactNode;
}

/**
 * A toolbar button that opens a menu of style controls. The menu opens below
 * the button. It has no diagram state: its sections take the values and
 * apply the changes.
 */
export function DiagramMenuButton({
  label,
  trigger,
  disabled = false,
  children,
}: DiagramMenuButtonProps) {
  return (
    <ButtonWithPopover
      variant="transparent"
      size="xs"
      disabled={disabled}
      label={trigger}
      aria-label={label}
      tooltip={label}
      highlightWhenOpen
      popperProps={{
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        PaperProps: { sx: { minWidth: 180 } },
      }}
    >
      {children}
    </ButtonWithPopover>
  );
}

export interface DiagramMenuOptionsProps<T> {
  /** The section heading. */
  label: string;
  options: readonly DiagramPickerOption<T>[];
  /**
   * The current value. Null when the selected elements have different
   * values: then no option shows a checkmark.
   */
  value: T | null;
  onChange: (value: T) => void;
}

/** A menu section with a heading and one option for each value. */
export function DiagramMenuOptions<T>({
  label,
  options,
  value,
  onChange,
}: DiagramMenuOptionsProps<T>) {
  return (
    <>
      <SelectItemGroupLabel label={label} />
      {options.map((option) => (
        <MenuItem
          key={String(option.value)}
          icon={option.icon}
          label={option.label}
          selected={option.value === value}
          onClick={() => onChange(option.value)}
        />
      ))}
    </>
  );
}

export interface DiagramMenuCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** A menu item that turns a setting on or off, with a checkbox. */
export function DiagramMenuCheckbox({
  label,
  checked,
  onChange,
}: DiagramMenuCheckboxProps) {
  return (
    <MenuItem
      icon={<StyledCheckbox value={checked} ariaLabel={label} tabIndex={-1} />}
      label={label}
      onClick={() => onChange(!checked)}
    />
  );
}

/** The line between two sections of a menu. */
export function DiagramMenuDivider() {
  return <Divider noMargin />;
}

export interface DiagramOptionPickerProps<T>
  extends DiagramMenuOptionsProps<T> {
  /** The trigger icon for mixed values. Defaults to the icon of the first option. */
  mixedIcon?: ReactNode;
  disabled?: boolean;
}

/**
 * A toolbar button with a menu of one set of options, such as line patterns.
 * The trigger shows the icon of the current option. The menu heading is also
 * the accessible name of the trigger.
 */
export function DiagramOptionPicker<T>({
  mixedIcon,
  disabled,
  ...section
}: DiagramOptionPickerProps<T>) {
  const { options, value, label } = section;
  const current = options.find((option) => option.value === value);
  return (
    <DiagramMenuButton
      label={label}
      trigger={current?.icon ?? mixedIcon ?? options[0]?.icon}
      disabled={disabled}
    >
      <DiagramMenuOptions {...section} />
    </DiagramMenuButton>
  );
}
